import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * Status-transition bitácora of the payments runtime. Backend contract (coloca-nest): `GET
 * /internal/payments/status-changes?operation_type=PAYIN|PAYOUT|SWAP&operation_id=<uuid>` — JWT, permission
 * `payments:read` AND **UserType INTERNAL only** (an ORGANIZATION_MEMBER gets 403). Rows come ordered oldest
 * → newest (`seq ASC`). Callers must NEVER hit this endpoint for org members — build the derived timeline
 * from the entity's own timestamps instead (see `OperationTimeline`).
 */

/**
 * Mirrors coloca-nest `PAYMENT_OPERATION_TYPES` — `PaymentDirection` **plus** `SWAP`. The DTO validates with
 * `@IsIn`, not `@IsEnum`, precisely because a swap has a status history but no payment direction (it moves
 * value between two wallets of the same organization, with no provider, rail or counterparty).
 */
export const PaymentOperationType = {
  PAYIN: "PAYIN",
  PAYOUT: "PAYOUT",
  SWAP: "SWAP",
} as const;
export type PaymentOperationType = (typeof PaymentOperationType)[keyof typeof PaymentOperationType];

// Mirrors coloca-nest `StatusChangeSource` (lowercase values on the wire).
export const StatusChangeSource = {
  INTERNAL_API: "internal_api",
  PROVIDER_WEBHOOK: "provider_webhook",
  SYSTEM: "system",
} as const;
export type StatusChangeSource = (typeof StatusChangeSource)[keyof typeof StatusChangeSource];

/** Mirrors coloca-nest `PaymentStatusChangeEntity` (append-only, no updated_at). */
export interface PaymentStatusChange {
  id: string;
  /** Monotonic insertion sequence (bigint serialized as string) — strict chronological order. */
  seq: string;
  operation_type: PaymentOperationType;
  operation_id: string;
  /** Null on the first transition (creation). Varchar — payins and payouts have different status sets. */
  previous_status: string | null;
  new_status: string;
  source: StatusChangeSource;
  actor_user_id: string | null;
  reason: string | null;
  metadata: { provider_reference?: string | null; ledger_entry_id?: string | null } | null;
  created_at: string;
}

export class PaymentsStatusChangesService {
  private static BASE_URL = "/internal/payments/status-changes";

  /** Full transition history of one operation, oldest first. INTERNAL staff only (403 for org members). */
  static async listByOperation(
    operationType: PaymentOperationType,
    operationId: string,
  ): Promise<AxiosResponse<PaymentStatusChange[]>> {
    const query = new URLSearchParams({ operation_type: operationType, operation_id: operationId });
    return HttpApiService.get<PaymentStatusChange[]>(`${this.BASE_URL}?${query}`);
  }
}
